import { withMutations, withResource } from '@angular-architects/ngrx-toolkit';
import { computed, inject } from '@angular/core';
import { createRxMutation, createRxResource } from '@next/core-lib/notification';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';

import { SalesOrderSchema, UpsertSalesOrderSchemaResponse } from '../models/sales-order-period.model';
import { SalesOrderPeriodsService } from '../services/sales-order-periods.service';

interface SalesOrderPeriodsState {
  selectedSalesOrderSchema: SalesOrderSchema | undefined;
}

const initialState: SalesOrderPeriodsState = {
  selectedSalesOrderSchema: undefined,
};

export const SalesOrderPeriodsStore = signalStore(
  withState(initialState),
  withResource(
    () => {
      const api = inject(SalesOrderPeriodsService);

      return {
        salesOrderSchemas: createRxResource<SalesOrderSchema[], void>({
          stream: () => api.getSalesOrderSchemas(),
          defaultValue: [],
          errorMessage: 'sales-order-periods.loadError',
        }),
      };
    },
    {
      errorHandling: 'previous value',
    },
  ),
  withMutations((store) => {
    const api = inject(SalesOrderPeriodsService);

    return {
      upsertSalesOrderSchema: createRxMutation<SalesOrderSchema, UpsertSalesOrderSchemaResponse>({
        operation: (salesOrderSchema) => api.upsertSalesOrderSchema(salesOrderSchema),
        notification: {
          successMessage: 'sales-order-periods.saveSuccess',
          errorMessage: 'sales-order-periods.saveError',
        },
        onSuccess: () => {
          patchState(store, { selectedSalesOrderSchema: undefined });
          store._salesOrderSchemasReload();
        },
      }),
    };
  }),
  withComputed((store) => ({
    schemas: computed(() => store.salesOrderSchemasValue() ?? []),
    isLoading: computed(() => store.salesOrderSchemasIsLoading()),
    loadFailed: computed(() => store.salesOrderSchemasError() !== undefined),
    isSaving: computed(() => store.upsertSalesOrderSchemaIsPending()),
    saveFailed: computed(() => store.upsertSalesOrderSchemaError() !== undefined),
  })),
  withMethods((store) => ({
    selectSalesOrderSchema: (salesOrderSchema: SalesOrderSchema) => {
      patchState(store, { selectedSalesOrderSchema: salesOrderSchema });
    },
    clearSelectedSalesOrderSchema: () => {
      patchState(store, { selectedSalesOrderSchema: undefined });
    },
    reload: () => {
      store._salesOrderSchemasReload();
    },
  })),
);
